import React from 'react';

import { useLazyLoadQuery, useRelayEnvironment } from 'react-relay';
import { fetchQuery, graphql } from 'relay-runtime';

import AddressEntryPage from '../AddressEntryPage';
import AddressErrorPage from '../AddressErrorPage';
import AddressSearchPage from '../AddressSearchPage';
import AddressSuggestionPage from '../AddressSuggestionPage';
import LoadingPage from '../LoadingPage';
import UnknownErrorPage from '../UnknownErrorPage';
import ResidentialAddressPage from './ResidentialAddressPage';
import { ResidentialAddressPagePrefillQuery as ResidentialAddressPagePrefillQueryType } from 'src/clients/Network/gql/build/ResidentialAddressPagePrefillQuery.graphql';
import { ResidentialAddressPageValidateAddressQuery as ResidentialAddressPageValidateAddressQueryType } from 'src/clients/Network/gql/build/ResidentialAddressPageValidateAddressQuery.graphql';
import { AddressInput, AddressType } from 'src/clients/Network/gql/types/address';
import { trackPageView } from 'src/clients/tracking';
import { NavigationContext } from 'src/components/creditApplication/NavigationProvider';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';

const ResidentialAddressPagePrefillQuery = graphql`
  query ResidentialAddressPagePrefillQuery {
    prefillIdentityDetails(q: {}) {
      data {
        residentialAddress {
          id
          streetAddress
          primaryAddressLine
          secondaryAddressLine
          locality
          subdivision
          zip
        }
      }
      errors
    }
  }
`;

const ResidentialAddressPageValidateAddressQuery = graphql`
  query ResidentialAddressPageValidateAddressQuery($q: AddressValidityRequest!) {
    addressValidity(q: $q) {
      address {
        id
        streetAddress
        primaryAddressLine
        secondaryAddressLine
        locality
        subdivision
        zip
      }
      addressFormatted {
        id
        streetAddress
        primaryAddressLine
        secondaryAddressLine
        locality
        subdivision
        zip
      }
      error
      errorSkippable
    }
  }
`;

export type ResidentialAddressPageType =
  | 'RESIDENTIAL_ADDRESS'
  | 'SEARCH_ADDRESS'
  | 'SUGGESTED_ADDRESS'
  | 'MANUAL_ADDRESS'
  | 'ADDRESS_VALIDATION_ERROR';

const ResidentialAddressRouter = () => {
  const relayEnv = useRelayEnvironment();
  const { nextPage } = React.useContext(NavigationContext);
  const { prefillIdentityDetails } = useLazyLoadQuery<ResidentialAddressPagePrefillQueryType>(
    ResidentialAddressPagePrefillQuery,
    {},
  );

  const residentialAddress = prefillIdentityDetails?.data?.residentialAddress;
  const [currentPage, setCurrentPage] = React.useState<ResidentialAddressPageType>('RESIDENTIAL_ADDRESS');
  const [enteredAddress, setEnteredAddress] = React.useState<AddressInput>();
  const [suggestedAddress, setSuggestedAddress] = React.useState<AddressType>();
  const [addressValidationError, setAddressValidationError] = React.useState<string>();
  const [addressValidationSkippable, setAddressValidationSkippable] = React.useState<boolean>();

  const [continueLoading, setContinueLoading] = React.useState(false);
  const [validateLoading, setValidateLoading] = React.useState(false);

  React.useEffect(() => {
    switch (currentPage) {
      case 'SUGGESTED_ADDRESS':
        trackPageView('ResidentialAddressSuggested');
        break;
      case 'ADDRESS_VALIDATION_ERROR':
        trackPageView('ResidentialAddressValidationError');
        break;
      case 'RESIDENTIAL_ADDRESS':
        trackPageView('ResidentialAddressPrefilled');
        break;
      case 'SEARCH_ADDRESS':
        trackPageView('ResidentialAddressSearch');
        break;
      case 'MANUAL_ADDRESS':
        trackPageView('ResidentialAddressManual');
        break;
      default:
        break;
    }
  }, [currentPage]);

  const onContinue = React.useCallback(
    async (address?: AddressInput) => {
      setContinueLoading(true);
      if (address == null) {
        showGlobalErrorAlert();
      } else {
        await nextPage();
        useGlobalStore.setState((info) => ({
          ...info,
          personalInformation: {
            ...info.personalInformation,
            residentialAddress: address,
          },
        }));
      }
      setContinueLoading(false);
    },
    [nextPage],
  );

  React.useEffect(() => {
    if (!residentialAddress?.primaryAddressLine) {
      setCurrentPage('SEARCH_ADDRESS');
    }
  }, [residentialAddress?.primaryAddressLine]);

  const onSelectAddress = React.useCallback((address: AddressInput) => {
    setEnteredAddress(address);
    setCurrentPage('MANUAL_ADDRESS');
  }, []);

  const onValidateAddress = React.useCallback(
    async (address: AddressInput) => {
      setValidateLoading(true);
      setEnteredAddress(address);

      try {
        const response = await fetchQuery<ResidentialAddressPageValidateAddressQueryType>(
          relayEnv,
          ResidentialAddressPageValidateAddressQuery,
          {
            q: {
              primaryAddressLine: address?.primaryAddressLine ?? '',
              secondaryAddressLine: address?.secondaryAddressLine ?? '',
              locality: address?.locality ?? '',
              subdivision: address?.subdivision ?? '',
              zip5: address?.zip5 ?? '',
              purpose: 'RESIDENTIAL',
            },
          },
          { fetchPolicy: 'network-only' },
        ).toPromise();

        if (response?.addressValidity?.error != null) {
          setAddressValidationError(response?.addressValidity?.error);
          setAddressValidationSkippable(response?.addressValidity?.errorSkippable);
          setCurrentPage('ADDRESS_VALIDATION_ERROR');
          setValidateLoading(false);
        } else if (response?.addressValidity?.addressFormatted != null) {
          setSuggestedAddress(response.addressValidity.addressFormatted);
          setCurrentPage('SUGGESTED_ADDRESS');
          setValidateLoading(false);
        } else {
          onContinue(address);
          setValidateLoading(false);
        }
      } catch (error) {
        // user can navigate back to address entry and retry flow
        setCurrentPage('ADDRESS_VALIDATION_ERROR');
      } finally {
        setValidateLoading(false);
      }
    },
    [relayEnv, setCurrentPage],
  );

  if (currentPage === 'RESIDENTIAL_ADDRESS' && residentialAddress != null && !!residentialAddress.primaryAddressLine) {
    return (
      <ResidentialAddressPage
        onEditAddress={() => setCurrentPage('SEARCH_ADDRESS')}
        onContinue={() => onContinue({ ...residentialAddress, zip5: residentialAddress.zip })}
        address={residentialAddress}
        loading={continueLoading}
      />
    );
  } else if (currentPage === 'SEARCH_ADDRESS') {
    return (
      <AddressSearchPage
        addressType='residential'
        onEnterManually={() => setCurrentPage('MANUAL_ADDRESS')}
        loading={validateLoading}
        onContinue={onSelectAddress}
      />
    );
  } else if (currentPage === 'SUGGESTED_ADDRESS' && !!suggestedAddress) {
    return (
      <AddressSuggestionPage
        suggestedAddress={suggestedAddress}
        onAccept={() => onContinue({ ...suggestedAddress, zip5: suggestedAddress.zip })}
        onReject={() => onContinue(enteredAddress)}
        loading={continueLoading}
      />
    );
  } else if (currentPage === 'MANUAL_ADDRESS') {
    return (
      <AddressEntryPage onContinue={onValidateAddress} loading={validateLoading} prefillAddress={enteredAddress} />
    );
  } else if (currentPage === 'ADDRESS_VALIDATION_ERROR') {
    return (
      <AddressErrorPage
        error={addressValidationError}
        skippable={addressValidationSkippable}
        purpose='residential'
        enteredAddress={enteredAddress}
        onEditAddress={() => setCurrentPage('SEARCH_ADDRESS')}
        onContinue={() => onContinue(enteredAddress ?? residentialAddress)}
        loading={continueLoading}
      />
    );
  } else {
    return <UnknownErrorPage />;
  }
};

const WithSuspense = () => {
  return (
    <React.Suspense fallback={<LoadingPage />}>
      <ResidentialAddressRouter />
    </React.Suspense>
  );
};

export default WithSuspense;
